export const notFound = {
  tag: 'main',
  id: 'main-content',
  extends: 'AppPage',
  flow: 'y',
  // Installed workspace APPS inject their pages after boot, so this page is
  // rendered first for any app route and then replaced once the module lands
  // (_rerouteIfStranded). Stamping "Not found" into the tab title during that
  // window makes a perfectly good app look broken while it loads — the title
  // flashed "Not found" on every cold load of an installed app's route.
  // A neutral title while apps are still activating; the real one only once
  // the route is genuinely unresolvable.
  //
  // The BODY needs the same treatment, not just the title. Gating the tab
  // stopped the browser tab from lying while the page still shouted "404 —
  // Page not found" at a route that was merely still loading, which is the
  // "sometimes it does not render" report. Below: the status code and the
  // recovery actions hide while activation is in flight, and the copy says
  // what is actually happening.
  //
  // `hide:` and `text:` on purpose — the two mechanisms that re-fire reliably
  // here. A state-driven STYLE prop would not (see tickets/smbls.md,
  // "css-in-props: a state-driven style prop is silently one-shot"), and `if:`
  // creates the element in an update pass, where it can miss its lifecycle.
  metadata: (el, s) =>
    s.root && s.root._wsAppsActivating
      ? { title: 'Symbols' } // the product name
      : { title: el.call('polyglot', 'canvas.page.notFound.title') },
  align: 'center center',
  height: '100%',
  width: '100%',
  padding: 'C',
  color: 'title',
  textAlign: 'center',
  gap: 'A',

  StatusCode: {
    tag: 'h1',
    hide: (el, s) => !!s.root?._wsAppsActivating,
    text: (el, s) => String(s.root?.notFoundStatus || 404),
    fontSize: 'E',
    fontWeight: '700',
    letterSpacing: '-0.04em',
    margin: '0',
    color: 'title'
  },
  // §9: "Loading…" strings are illegal. This is the legal full-surface
  // indeterminate case (waiting on a workspace app module to activate) —
  // the house Spinner replaces the retired text. Key matches the extended
  // component (Rule: key-based auto-extend) so no `extends:` is needed.
  LoadingSpinner: {
    hide: (el, s) => !s.root?._wsAppsActivating,
    boxSize: 'iconXl'
  },
  Title: {
    tag: 'h2',
    hide: (el, s) => !!s.root?._wsAppsActivating,
    text: (el, s) => s.root?.notFoundTitle || 'Page not found',
    fontSize: 'B',
    fontWeight: '700',
    margin: '0',
    color: 'title'
  },
  Message: {
    tag: 'p',
    text: (el, s) =>
      s.root?._wsAppsActivating
        ? 'Opening this workspace app.'
        : s.root?.notFoundMessage ||
          "The page you're looking for doesn't exist. It may have been moved or the URL may be incorrect.",
    maxWidth: 'I',
    margin: '0 auto',
    color: 'caption',
    fontSize: 'Z1',
    lineHeight: '1.5em'
  },

  Actions: {
    // Offering "Back to dashboard" mid-load invites the user to leave a page
    // that was about to work.
    hide: (el, s) => !!s.root?._wsAppsActivating,
    flow: 'x',
    gap: 'Y',
    align: 'center',
    marginTop: 'A',

    // Shown only when the blocker is auth — the recovery is to sign in.
    SignIn: {
      extends: 'Button',
      if: (el, s) => s.root?.notFoundStatus === 401,
      text: '{{ canvas.notFound.signIn | polyglot }}',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      border: '0',
      background: 'brand',
      color: 'title',
      ':hover': { background: 'brand+10' },
      onClick: (e, el) => el.router('/signin', el.getRoot())
    },
    // Shown for any retryable error (5xx, 429, network). SPA-RELOAD-1: this
    // used to call `reloadPage` (a raw `window.location.reload()`), which tore
    // down the whole workspace shell — every loaded app module, the editor
    // graph and the session-scoped state — to re-issue ONE project fetch that
    // `ensureProjectLoaded` can re-issue in place. Clearing `projectNotFound`
    // first swaps this page out for the canvas loading chrome; a second
    // failure re-sets it from the loader's own error classification.
    Retry: {
      extends: 'Button',
      if: (el, s) => {
        const code = s.root?.notFoundStatus
        return code === 429 || (code >= 500 && code < 600)
      },
      text: '{{ canvas.notFound.retry | polyglot }}',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      fontSize: 'Z2',
      cursor: 'pointer',
      border: '0',
      background: 'overlayStrong',
      color: 'title',
      ':hover': { background: 'hover' },
      // `ensureProjectLoaded` lives in the canvas EDITOR's function graph,
      // which the workspace shell deliberately does not register (it imports
      // the slim `functions/eagerSlice.js`). `el.call` on a missing function
      // silently no-ops, so mirror pages/main.js's on-demand import fallback
      // rather than a static import — this page IS in the eager chunk.
      onClick: (e, el, s) => {
        if (s.root && typeof s.root.update === 'function') {
          s.root.update({ projectNotFound: null })
        }
        if (typeof el.context?.functions?.ensureProjectLoaded === 'function') {
          return el.call('ensureProjectLoaded', el, s, { force: true })
        }
        return import('../functions/canvas/navigation.js')
          .then((m) => m.ensureProjectLoaded(el, s, { force: true }))
          .catch((err) => console.warn('[canvas] project loader unavailable:', err?.message || err))
      }
    },

    Back: {
      extends: 'Link',
      href: '/',
      text: '{{ canvas.notFound.backToDashboard | polyglot }}',
      display: 'inline-flex',
      align: 'center center',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      fontSize: 'Z2',
      cursor: 'pointer',
      border: '0',
      background: 'overlayStrong',
      color: 'title',
      ':hover': { background: 'hover' }
    }
  }
}
